import { flexRender, useReactTable, getCoreRowModel, getPaginationRowModel } from "@tanstack/react-table";
import type { ClassValue } from "clsx";
import TextTooltip from "components/shared/TextTooltip";
import classNames from "../classNames";
import type { ITableProps } from "./interface";

function Table<T extends Record<string, any>>({
  data,
  columns,
  className,
  pageSize = 15,
  isOddEven,
  isSticky = true,
  isPaginationEnabled = false,
  isMobileTableDiasbled = false,
}: ITableProps<T>) {
  const { getState, getHeaderGroups, getRowModel, getPageOptions, setPageIndex } = useReactTable({
    columns,
    data,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: !isPaginationEnabled,
    getPaginationRowModel: getPaginationRowModel(),
    initialState: {
      pagination: {
        pageSize,
      },
    },
  });

  const { rows } = getRowModel();
  const currentPage = getState().pagination.pageIndex;
  const pages = getPageOptions();

  return (
    <div className="inline-block w-full align-middle">
      <div className="touch-auto space-y-2">
        <table className={classNames("min-w-full border-collapse border-spacing-0 rounded-md", className)}>
          <thead className="group/thead select-none">
            {getHeaderGroups().map((headerGroup) => (
              <tr
                key={`table-header-${headerGroup.id}`}
                className={classNames("group/tr sm:contents", {
                  hidden: !isMobileTableDiasbled,
                })}
              >
                {headerGroup.headers
                  .filter((header) => !header.column.columnDef.meta?.isHidden)
                  .map((header) => {
                    const { columnDef } = header.column;

                    let styleForBreakpoint: ClassValue | null = null;
                    if (columnDef && columnDef.meta && columnDef.meta.breakpoint) {
                      styleForBreakpoint = {
                        [`hidden ${columnDef.meta.breakpoint}:table-cell`]: !!columnDef.meta.breakpoint,
                      };
                    }

                    return (
                      <th
                        scope="col"
                        key={header.id}
                        className={classNames(
                          "group-last-of-type/thead:group-last-of-type/tr:first-of-type:rounded-tl-md",
                          "group-last-of-type/thead:group-last-of-type/tr:last-of-type:rounded-tr-md",
                          "bg-slate-100 p-3 text-center text-sm font-semibold text-gray-900",
                          columnDef.meta?.header?.className,
                          styleForBreakpoint,
                          { "sticky top-navbar-mobile-offset lg:top-navbar-desktop-offset": isSticky },
                        )}
                      >
                        {columnDef.meta?.header?.tooltip ? (
                          <TextTooltip
                            id={`tooltips/th/netIncome/${columnDef.header}`}
                            placement="top"
                            content={columnDef.meta.header.tooltip}
                            style={{
                              maxWidth: 260,
                              textAlign: "center",
                            }}
                          >
                            {({ accessibility, bind, ref }) => (
                              <span {...accessibility} {...bind} ref={ref}>
                                {flexRender(columnDef.header, header.getContext())}
                              </span>
                            )}
                          </TextTooltip>
                        ) : (
                          flexRender(columnDef.header, header.getContext())
                        )}
                      </th>
                    );
                  })}
              </tr>
            ))}
          </thead>

          <tbody className="group/tbody">
            {rows.map((row) => {
              return (
                <tr
                  key={row.id}
                  className={classNames(
                    "group/tr odd:bg-white even:bg-slate-100 sm:odd:bg-transparent sm:even:bg-transparent",
                    {
                      "sm:odd:bg-white sm:even:bg-slate-100": isOddEven,
                    },
                  )}
                >
                  {row
                    .getVisibleCells()
                    .filter((cell) => !cell.column.columnDef.meta?.isHidden)
                    .map((cell, i) => {
                      const { columnDef } = cell.column;

                      let cellClassName: string | undefined;
                      if (typeof columnDef.meta?.cell?.className === "function") {
                        cellClassName = columnDef.meta?.cell?.className(cell.getContext());
                      } else {
                        cellClassName = columnDef.meta?.cell?.className;
                      }

                      let styleForBreakpoint: ClassValue | null = null;
                      if (columnDef && columnDef.meta && columnDef.meta.breakpoint) {
                        styleForBreakpoint = {
                          [`hidden ${columnDef.meta.breakpoint}:table-cell`]: !!columnDef.meta.breakpoint,
                        };
                      }

                      return (
                        <td
                          key={cell.id}
                          className={classNames(
                            "whitespace-nowrap px-3 py-1.5",
                            "group-last-of-type/tbody:group-last-of-type/tr:first-of-type:rounded-bl-md",
                            "group-last-of-type/tbody:group-last-of-type/tr:last-of-type:rounded-br-md",
                            "text-left text-sm font-medium text-gray-900 sm:text-center",
                            cellClassName,
                            styleForBreakpoint,
                          )}
                        >
                          <p
                            className={classNames("sm:contents", {
                              hidden: !isMobileTableDiasbled,
                            })}
                          >
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </p>
                          {i === 0 && !isMobileTableDiasbled && (
                            <dl className="grid grid-cols-2 font-normal">
                              {row
                                .getVisibleCells()
                                .filter((cell) => !cell.column.columnDef.meta?.isHidden)
                                .map((mobileCell) => {
                                  const { columnDef: mobileColumnDef } = mobileCell.column;

                                  const style = mobileColumnDef?.meta?.breakpoint
                                    ? {
                                        [`${mobileColumnDef.meta.breakpoint}:hidden`]:
                                          !!mobileColumnDef.meta.breakpoint,
                                      }
                                    : "sm:hidden";

                                  return (
                                    <dl key={mobileCell.id} className="contents">
                                      <dt
                                        className={classNames(
                                          "mt-1 table-cell truncate font-semibold text-gray-700",
                                          style,
                                        )}
                                      >
                                        {mobileColumnDef.header?.toString()}
                                      </dt>
                                      <dd className={classNames("mt-1 table-cell truncate text-gray-500", style)}>
                                        {flexRender(mobileColumnDef.cell, mobileCell.getContext())}
                                      </dd>
                                    </dl>
                                  );
                                })}
                            </dl>
                          )}
                        </td>
                      );
                    })}
                </tr>
              );
            })}
          </tbody>
        </table>

        {isPaginationEnabled && pages.length > 1 && (
          <div className="flex space-x-2">
            {pages.map((page) => (
              <button
                className={classNames(
                  "m-0 inline-flex h-8 w-8 cursor-pointer select-none items-center justify-center rounded-xl border-none p-0 text-sm font-bold tracking-wide outline-none",
                  {
                    "bg-brand text-white": currentPage === page,
                    "bg-neutral-100 text-brand": currentPage !== page,
                  },
                )}
                type="button"
                key={page}
                onClick={() => setPageIndex(page)}
              >
                {page + 1}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default Table;
